import React from 'react'
import DOMPurify from 'dompurify'

import {
  Box,
  Display,
  FlexDirection,
  FontFamily,
  FontWeight,
  GridDisplay,
  Heading1,
  Text,
  ZIndex,
} from '@gousto-internal/citrus-react'
import type { CampaignBannerHeaderContentBlock } from '@library/api-menu-experience'

import { CampaignBannerCtaCard } from './CampaignBannerCtaCard'
import {
  getBackgroundImageUrl,
  type MerchandisingBannerActionProps,
  ScrollableRail,
} from './campaignBanner.atoms'
import { CampaignBannerRecipeCard } from './CampaignBannerRecipeCard'

type CampaignBannerProps = {
  contentBlock: CampaignBannerHeaderContentBlock
} & MerchandisingBannerActionProps

export const CampaignBanner = ({
  contentBlock,
  getRecipeCardState,
  canAddRecipes,
  reachedLimit,
  onAddClick,
  onBlockedAddAttempt,
  onCtaClick,
  onRecipeClick,
  onRemoveClick,
  renderFlexPortionBadge,
  renderFlexPortionEditor,
}: CampaignBannerProps) => {
  const backgroundImageUrl = getBackgroundImageUrl(contentBlock.backgroundImage.crops)

  return (
    <Box
      data-testid="campaign-banner"
      paddingH={[0, 0, 6]}
      paddingBottom={[0, 0, 8]}
      display={GridDisplay.Grid}
      gridTemplateColumns={['auto', 'auto', 'fit-content(40%) auto']}
      gridTemplateRows={['auto 6fr 1fr', 'auto 6fr 1fr', 'auto auto']}
    >
      <Box
        gridColumn={'1 / span 2'}
        gridRow={['1 / span 2', '1 / span 2', '1']}
        zIndex={ZIndex.Auto}
        minHeight={['none', 'none', '300px']}
        style={{
          backgroundColor: contentBlock.backgroundColor,
          backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : undefined,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        }}
      ></Box>
      <Box
        gridColumn={'1'}
        gridRow={'1'}
        zIndex={ZIndex.Default}
        paddingV={[3, 3, 8]}
        paddingH={[4, 4, 8]}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={3}
      >
        <Heading1
          size={[13, 13, 15]}
          fontFamily={FontFamily.Bold}
          fontWeight={FontWeight.Bold}
          style={{
            color: contentBlock.textColor,
          }}
        >
          {contentBlock.title}
        </Heading1>
        <Text
          style={{
            color: contentBlock.textColor,
            lineHeight: '24px',
          }}
        >
          {contentBlock.description}
        </Text>
      </Box>
      <Box
        gridColumn={['1', '1', '2']}
        gridRow={['2 / span 2', '2 / span 2', '1 / span 2']}
        zIndex={ZIndex.Default}
        minWidth={'0'}
        paddingV={[4, 4, 8]}
        paddingH={[4, 4, 0]}
        style={{ paddingBottom: 0, paddingRight: 0 }}
      >
        <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={3}>
          <Text
            style={{
              color: contentBlock.textColor,
              paddingRight: '16px',
            }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contentBlock.subtitle) }}
          />
          <ScrollableRail gap={3}>
            {contentBlock.items.map((item, index) => {
              if (item.type === 'cta') {
                return (
                  <CampaignBannerCtaCard
                    key={`cta-${item.targetCategoryId}-${item.title}-${item.ctaText}`}
                    item={item}
                    onClick={onCtaClick}
                  />
                )
              }

              const cardState = getRecipeCardState(item.id)

              return (
                <CampaignBannerRecipeCard
                  key={item.id}
                  recipe={item}
                  canAddRecipes={canAddRecipes}
                  reachedLimit={reachedLimit}
                  onAddClick={onAddClick}
                  onBlockedAddAttempt={onBlockedAddAttempt}
                  onClick={onRecipeClick}
                  onRemoveClick={onRemoveClick}
                  index={index + 1}
                  isInBasket={cardState.isInBasket}
                  isOutOfStock={cardState.isOutOfStock}
                  renderFlexPortionBadge={renderFlexPortionBadge}
                  renderFlexPortionEditor={renderFlexPortionEditor}
                />
              )
            })}
          </ScrollableRail>
        </Box>
      </Box>
    </Box>
  )
}
